import React from 'react';
import { ClinicInfo } from '../../api/provider';
import { generatePaginationItems } from '../utils/dashboardHelpers';

export const ErrorNotice = ({
  error,
  onDismiss,
}: {
  error: string | null;
  onDismiss: () => void;
}) => {
  if (!error) return null;
  return (
    <div className="error-notification">
      <div className="error-content">
        <span className="error-icon">&#9888;</span>
        <p className="error-message">{error}</p>
      </div>
      <button className="dismiss-error" onClick={onDismiss}>
        &times;
      </button>
    </div>
  );
};

export const SearchBar = ({
  value,
  onChange,
  onSearch,
  onKeyDown,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  onSearch: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  placeholder: string;
}) => (
  <div className="search-container">
    <div className="search-box">
      <span className="search-icon">&#128269;</span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className="search-input"
      />
      <button className="search-button" onClick={onSearch}>
        Search
      </button>
    </div>
  </div>
);

export const Pagination = ({
  totalPages,
  currentPage,
  onChange,
}: {
  totalPages: number;
  currentPage: number;
  onChange: (page: number) => void;
}) => {
  if (totalPages <= 1) return null;

  return (
    <div className="pagination-container">
      <div className="pagination">
        {currentPage > 1 && (
          <button className="pagination-btn prev-btn" onClick={() => onChange(currentPage - 1)}>
            &lt; Previous
          </button>
        )}

        <div className="page-numbers">
          {generatePaginationItems(totalPages, currentPage).map((item) => {
            const itemKey = typeof item === 'number' ? `page-${item}` : 'ellipsis';
            let className = 'page-number';
            if (item === currentPage) {
              className = 'page-number current-page';
            } else if (item === '...') {
              className = 'page-number ellipsis';
            }
            return (
              <button
                key={itemKey}
                className={className}
                onClick={() => (typeof item === 'number' ? onChange(item) : undefined)}
                disabled={item === '...'}
              >
                {item}
              </button>
            );
          })}
        </div>

        {currentPage < totalPages && (
          <button className="pagination-btn next-btn" onClick={() => onChange(currentPage + 1)}>
            Next &gt;
          </button>
        )}
      </div>
    </div>
  );
};

export const FilterSidebar = ({
  options,
  selectedFilter,
  onChange,
  clinic,
}: {
  options: Array<{ label: string; value: string | null }>;
  selectedFilter: string | null;
  onChange: (value: string | null) => void;
  clinic: ClinicInfo | null;
}) => (
  <div className="filter-sidebar">
    <div className="filter-header">
      <h3>Filters</h3>
    </div>

    <div className="filter-section">
      <h4>Filter by Status</h4>
      <div className="filter-options">
        {options.map((option) => (
          <button
            key={option.label}
            className={`filter-option ${selectedFilter === option.value ? 'selected' : ''}`}
            onClick={() => onChange(option.value)}
          >
            <span className="filter-label">{option.label}</span>
          </button>
        ))}
      </div>
    </div>

    <div className="clinic-info-section">
      <h4>Clinic Information</h4>
      {clinic ? (
        <div className="clinic-card">
          <div className="clinic-name">{clinic.name}</div>
          {clinic.contact_email && (
            <div className="clinic-contact">
              <span className="contact-icon">&#128231;</span>
              {clinic.contact_email}
            </div>
          )}
          {clinic.contact_phone && (
            <div className="clinic-contact">
              <span className="contact-icon">&#128222;</span>
              {clinic.contact_phone}
            </div>
          )}
        </div>
      ) : (
        <div className="clinic-loading">
          <div className="loading-spinner-small"></div>
          <span>Loading clinic information...</span>
        </div>
      )}
    </div>
  </div>
);

